import { useState, useEffect, useRef, useCallback } from 'react';
import { cloneDeep } from 'es-toolkit/object';;
import {
  SalesforceConnection,
  SalesforceConnectionEdition,
} from '@modules/salesforce/models';
import { OAUTH } from '@modules/salesforce/shared/constants';

const POLL_INTERVAL = 1000;
interface UseOAuthFlowProps {
  /** Callback triggered upon successful OAuth authentication. */
  onAuthComplete: (data: SalesforceConnection) => void;
  /** Callback triggered upon OAuth authentication failure. */
  onAuthError: (data: SalesforceConnection) => void;
}

type AuthEventListenerType = (event: MessageEvent) => void;

/**
 * This hook performs Salesforce authentication using OAuth by opening an auth window and listening for events. It polls to monitor the window’s state, ensuring that it responds appropriately if the window closes without the handleOAuthEvent being triggered. The event handler is registered on the window with a message event.
 * @param props.onAuthComplete Callback invoked upon successful completion of the authentication process.
 * @param props.onAuthError Callback invoked when the authentication process fails.
 * @returns object:
 *  - **isConnecting:** Indicates whether the authentication process is currently running, and is used to update the UI when the process is active/inactive.
 *  - **handleOAuthLogin:** Provides a handler method to execute the authentication process for the OAuth login method.
 */
export const useOAuthFlow = (props: UseOAuthFlowProps) => {
  const { onAuthComplete, onAuthError } = props;
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const popupRef = useRef<Window | null>(null);
  const timerRef = useRef<number | null>(null);
  const authEventListener = useRef<AuthEventListenerType | null>(null);

  /**
   * “Launches the OAuth window for Salesforce to initiate the authentication process.
   * @param url URL for the authentication window to open.
   */
  const openOAuthPopup = (url: string) => {
    const width = 600;
    const height = 400;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    popupRef.current = window.open(
      url,
      'Salesforce OAuth',
      `width=${width}, height=${height}, top=${top}, left=${left}`,
    );
  };

  /**
   * Monitors the OAuth popup to check if it closes without the handleOAuthEvent being triggered on the window’s message event listener. This handles edge cases where the user closes the window manually or it closes unexpectedly, allowing us to reset the authentication process
   * */
  const checkIfPopupClosed = () => {
    timerRef.current = window.setInterval(() => {
      if (popupRef.current?.closed) {
        clearInterval(timerRef.current!);
        setIsConnecting(false);
      }
    }, POLL_INTERVAL);
  };

  /**
   * Callback for OAuth events. It checks if the event contains an `isAuthCallback` field or if there is an error to determine whether the event is a success or failure. It extracts the `code` from the data payload received in the callback and attaches it to the sfConnection data, which must be sent to the backend for further get access token based on it.
   */
  const handleOAuthEvent = useCallback(
    (event: MessageEvent, sfConnectionData: SalesforceConnection) => {
      const data = cloneDeep(sfConnectionData);

      if (event?.data?.isAuthCallback) {
        const queryString = event?.data?.queryString;
        const params = new URLSearchParams(queryString);
        const error = params.get('error');
        const code = params.get('code');

        if (error) {
          onAuthError(data);
          return;
        }
        if (code) {
          data.authCode = code;
          onAuthComplete(data);
          popupRef.current?.close();
          return;
        }
      }
    },
    [onAuthComplete, onAuthError],
  );

  /**
   * Constructs the authentication URL based on the user’s environment selection (sandbox or production). It includes the `clientId` and `redirect_uri` specified by the user in the connection form for Salesforce and appends them to the URL. It then calls `openOAuthPopup` with the generated URL and begins polling to check the popup’s open status using `checkIfPopupClosed`.
   * @param sfConnectionData Salesforce connection information entered by the user for the purpose of authorization.
   */
  const handleConnectToSalesforce = (
    sfConnectionData: SalesforceConnection,
  ) => {
    const authURL = sfConnectionData?.isSandbox
      ? OAUTH.SANDBOX_AUTH_URL
      : OAUTH.AUTH_URL;
    const queryParams = {
      client_id: sfConnectionData?.clientId,
      redirect_uri: sfConnectionData?.callbackUrl,
      response_type: 'code',
      prompt: 'login',
    };
    const queryString = new URLSearchParams(queryParams).toString();
    const authorizationUrl = authURL + `?${queryString}`;

    if (!authEventListener.current) {
      authEventListener.current = (event) =>
        handleOAuthEvent(event, sfConnectionData);
      window.addEventListener('message', authEventListener.current);
    }

    openOAuthPopup(authorizationUrl);
    checkIfPopupClosed();
  };

  /**
   * A handler method to execute the authentication process for the OAuth login method. Calls `handleConnectToSalesforce` to start the authentication process
   * @param sfConnectionData Salesforce connection information entered by the user for the purpose of authorization.
   */
  const handleOAuthLogin = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.isSandbox =
      data.salesforceConnectionEdition == SalesforceConnectionEdition.SANDBOX;
    handleConnectToSalesforce(data);
  };

  /**
   * Cleanup hook to remove:
   * - The message listener from the window
   * - The interval reference used for polling the popup window’s open status”
   */
  useEffect(() => {
    return () => {
      window.removeEventListener('message', authEventListener.current);
      clearInterval(timerRef.current);
    };
  }, []);

  return { handleOAuthLogin, isConnecting };
};
